/**
 * Renderers for the steps of a coding turn (spec 083 §6).
 *
 * Three vocabularies arrive from the sandbox and all three are drawn as the one
 * `CodingToolRow`: shell commands and touched files come as typed `data-*` parts
 * (the AI SDK has no native chunk for them), and everything else the engine does
 * comes as a `dynamic-tool` part. A reader should not be able to tell which is
 * which — they are all just steps the agent took.
 *
 * @module components/chat/messages/coding-parts
 */

import {
  FileText,
  FolderTree,
  Globe,
  ListChecks,
  Pencil,
  Search,
  Terminal,
  Users,
} from "lucide-react";
import type { ReactNode } from "react";

import { requestDiffPanel } from "@/components/coding/open-diff-panel";
import { CodingRowOutput, CodingToolRow, type CodingRowState } from "./coding-tool-row";
import { formatToolName } from "./tool-call-message/format-tool-name";

/** Payload of a `data-file-change` part. */
export interface CodingFileChangeData {
  path?: string;
  status?: "added" | "modified" | "deleted" | "renamed" | "binary";
  additions?: number;
  deletions?: number;
  patchId?: string;
}

/** Payload of a `data-command` part. */
export interface CodingCommandData {
  command?: string;
  exitCode?: number;
  preview?: string;
  outputId?: string;
}

/** Payload of a `data-plan` part. */
export interface CodingPlanData {
  items?: Array<{ text: string; status: "pending" | "active" | "done" }>;
}

/**
 * Strips the sandbox mount point from a reported path.
 *
 * The engine reports absolute container paths (`/workspace/acme-api/src/app.py`)
 * which say nothing a user needs. Only the mount point is removed, not the
 * repository directory under it: a session can hold several repositories, and
 * `acme-api/src/app.py` is what distinguishes them.
 */
function displayPath(path: string | undefined): string {
  if (path === undefined || path.length === 0) return "";
  return path.replace(/^\/workspace\//, "");
}

/** One shell command: the command line, its output behind the row. */
export function CodingCommandPart({ data }: { data: CodingCommandData }) {
  const finished = data.exitCode !== undefined;
  const output = data.preview ?? "";
  const command = data.command ?? "";

  return (
    <CodingToolRow
      data-testid="coding-part-command"
      icon={<Terminal className="size-3.5" />}
      label={finished ? "Ran command" : "Running command"}
      detail={command}
      state={finished ? (data.exitCode === 0 ? "ok" : "error") : "running"}
    >
      {output.length > 0 ? <CodingRowOutput>{output}</CodingRowOutput> : undefined}
    </CodingToolRow>
  );
}

/** Verb for a change, so the row says what actually happened to the file. */
const STATUS_VERB: Record<string, string> = {
  added: "File created",
  deleted: "File deleted",
  renamed: "File renamed",
  modified: "File change",
  binary: "File change",
};

/**
 * One file the agent touched.
 *
 * Expanding shows the path and the size of the change, and offers the Changes
 * panel — which is where a diff is actually read. Line counts appear only when
 * they are real: the sandbox announces a file the moment it sees a patch, before
 * it knows the size of the change, and sends zeroes until the authoritative
 * counts arrive from its own `GET /diff`. Rendering those zeroes claimed every
 * file was a no-op edit.
 */
export function CodingFileChangePart({ data }: { data: CodingFileChangeData }) {
  const path = displayPath(data.path);
  const additions = data.additions ?? 0;
  const deletions = data.deletions ?? 0;

  return (
    <CodingToolRow
      data-testid="coding-part-file-change"
      icon={<Pencil className="size-3.5" />}
      label={STATUS_VERB[data.status ?? "modified"] ?? "File change"}
      detail={path}
      state="ok"
    >
      <div className="flex items-center gap-3 text-xs">
        <span className="min-w-0 truncate font-mono text-muted-foreground">{path}</span>
        {(additions > 0 || deletions > 0) && (
          <span className="shrink-0 tabular-nums">
            {additions > 0 && <span className="text-success">+{additions}</span>}
            {additions > 0 && deletions > 0 && " "}
            {deletions > 0 && <span className="text-destructive">-{deletions}</span>}
          </span>
        )}
        <button
          type="button"
          data-testid="coding-part-file-change-open-diff"
          onClick={requestDiffPanel}
          className="shrink-0 underline-offset-2 transition-colors hover:text-foreground hover:underline"
        >
          Open in Changes
        </button>
      </div>
    </CodingToolRow>
  );
}

/** The agent's task list. */
export function CodingPlanPart({ data }: { data: CodingPlanData }) {
  const items = data.items ?? [];
  if (items.length === 0) return null;

  const done = items.filter((item) => item.status === "done").length;

  return (
    <CodingToolRow
      data-testid="coding-part-plan"
      icon={<ListChecks className="size-3.5" />}
      label="Updated plan"
      detail={`${done}/${items.length} done`}
      state="ok"
    >
      <ul className="flex flex-col gap-1 text-xs">
        {items.map((item, index) => (
          <li key={`${item.text}-${index}`} className="flex items-center gap-2">
            <span
              className={
                item.status === "done"
                  ? "text-muted-foreground line-through"
                  : item.status === "active"
                    ? "text-foreground"
                    : "text-muted-foreground"
              }
            >
              {item.text}
            </span>
          </li>
        ))}
      </ul>
    </CodingToolRow>
  );
}

/**
 * How each of the engine's tools reads as a row (opencode-ai@1.18.11).
 *
 * The action and its subject are separated deliberately — see `CodingToolRow`.
 * `bash` is absent because a shell call arrives as a `data-command` part, never
 * as a tool call.
 */
const CODING_TOOL_ROWS: Record<
  string,
  { label: string; running?: string; icon: ReactNode; detail?: (input: Record<string, unknown>) => string | undefined }
> = {
  read: {
    label: "Read file",
    running: "Reading file",
    icon: <FileText className="size-3.5" />,
    detail: (input) => pathOf(input),
  },
  write: {
    label: "Wrote file",
    running: "Writing file",
    icon: <Pencil className="size-3.5" />,
    detail: (input) => pathOf(input),
  },
  edit: {
    label: "Edited file",
    running: "Editing file",
    icon: <Pencil className="size-3.5" />,
    detail: (input) => pathOf(input),
  },
  patch: {
    label: "Applied patch",
    running: "Applying patch",
    icon: <Pencil className="size-3.5" />,
  },
  grep: {
    label: "Searched",
    running: "Searching",
    icon: <Search className="size-3.5" />,
    detail: (input) => stringOf(input, "pattern"),
  },
  glob: {
    label: "Found files",
    running: "Finding files",
    icon: <Search className="size-3.5" />,
    detail: (input) => stringOf(input, "pattern"),
  },
  list: {
    label: "Listed directory",
    running: "Listing directory",
    icon: <FolderTree className="size-3.5" />,
    detail: (input) => pathOf(input),
  },
  webfetch: {
    label: "Fetched page",
    running: "Fetching page",
    icon: <Globe className="size-3.5" />,
    detail: (input) => stringOf(input, "url"),
  },
  todowrite: {
    label: "Updated plan",
    running: "Updating plan",
    icon: <ListChecks className="size-3.5" />,
    detail: (input) => planProgress(input),
  },
  todoread: { label: "Read plan", icon: <ListChecks className="size-3.5" /> },
  task: {
    label: "Delegated",
    running: "Delegating",
    icon: <Users className="size-3.5" />,
    detail: (input) => stringOf(input, "description"),
  },
};

function stringOf(input: Record<string, unknown>, field: string): string | undefined {
  const value = input[field];
  if (typeof value !== "string" || value.length === 0) return undefined;
  return value;
}

function pathOf(input: Record<string, unknown>): string | undefined {
  const raw =
    stringOf(input, "filePath") ?? stringOf(input, "file_path") ?? stringOf(input, "path");
  return raw === undefined ? undefined : displayPath(raw);
}

function planProgress(input: Record<string, unknown>): string | undefined {
  const todos = input.todos;
  if (!Array.isArray(todos) || todos.length === 0) return undefined;
  const done = todos.filter((todo) => (todo as { status?: unknown })?.status === "completed").length;
  return `${done}/${todos.length} done`;
}

/** The shape a tool part arrives in, narrowed to what a row needs. */
interface CodingToolPart {
  toolName?: string;
  state?: string;
  input?: Record<string, unknown>;
  output?: unknown;
  errorText?: string;
}

/**
 * Whether a tool call finished, and how it went.
 *
 * `output-available` and `output-error` are the AI SDK's terminal states, but a
 * stored message may carry neither: turns persisted before `convert.ts` learned
 * to leave tool state alone wrote `done` over every part, which is the *text*
 * lifecycle's terminal value and meaningless for a call. Those rows are not
 * failures, and reading them as such put a red cross on every step of every turn
 * already in the database.
 *
 * So the presence of output decides it. A call that produced output returned,
 * whatever its state field says — including `null`, which is what a successful
 * call with nothing to report records. Only an explicit error state is a
 * failure; a call with no output at all is either still running or was cut off.
 */
export function codingRowState(
  part: { state?: string; output?: unknown },
  isMessageStreaming: boolean,
): CodingRowState {
  if (part.state === "output-error") return "error";
  if (part.state === "output-available" || part.output !== undefined) return "ok";
  return isMessageStreaming ? "running" : "error";
}

/**
 * One tool call the engine made.
 *
 * Used in place of `ToolCallMessage` on this surface only. That component
 * renders the platform's whole tool vocabulary — artifacts, agent calls, browser
 * screenshots, rendered forms — none of which a coding sandbox emits, and its
 * row is shaped for a chat answer rather than for a column of build steps.
 */
export function CodingToolCallPart({
  part,
  isMessageStreaming,
}: {
  part: CodingToolPart;
  isMessageStreaming: boolean;
}) {
  const toolName = part.toolName ?? "";
  const row = CODING_TOOL_ROWS[toolName];
  const input = part.input ?? {};

  const state = codingRowState(part, isMessageStreaming);

  const label =
    state === "running" && row?.running !== undefined
      ? row.running
      : (row?.label ?? formatToolName(toolName));

  const output =
    part.state === "output-error"
      ? (part.errorText ?? "The tool call failed.")
      : typeof part.output === "string"
        ? part.output
        : part.output === undefined || part.output === null
          ? ""
          : JSON.stringify(part.output, null, 2);

  return (
    <CodingToolRow
      data-testid="coding-part-tool-call"
      icon={row?.icon ?? <Terminal className="size-3.5" />}
      label={label}
      detail={row?.detail?.(input) ?? fallbackDetail(input)}
      state={state}
    >
      {output.length > 0 ? <CodingRowOutput>{output}</CodingRowOutput> : undefined}
    </CodingToolRow>
  );
}

/** Best-effort subject for a tool with no entry of its own. */
function fallbackDetail(input: Record<string, unknown>): string | undefined {
  for (const field of ["filePath", "path", "pattern", "query", "url", "command", "description"]) {
    const value = stringOf(input, field);
    if (value !== undefined) return value;
  }
  return undefined;
}

/** Part types this module draws; used by the activity-segment classifier. */
export const CODING_PART_TYPES = new Set([
  "data-file-change",
  "data-command",
  "data-plan",
]);
